import { useCallback, useState } from "react"
import type { PreOrder } from "@stockline/shared/types/orders/pre-order"
import type { PreOrderFlow } from "@stockline/shared/types/orders/pre-order-flow"
import { HttpError } from "../../../lib/offline/adapters/fetch-http-client"
import { PRE_ORDER_TABLE } from "../../../lib/offline/resources/orders/pre-order/db"
import { PRE_ORDER_FLOW_TABLE } from "../../../lib/offline/resources/orders/pre-order-flow/db"
import type { StorageAdapter } from "../../../lib/offline/types"
import { useAISync } from "../../ai/providers/AISyncProvider"

/** Server `error` codes on a push-batch 409 that mean "your view is stale". */
const STALE_EXPORT_CONFLICT_ERRORS = new Set([
  "export_already_succeeded",
  "export_in_progress",
])

/**
 * True when a push-batch rejection is a 409 staleness signal — the server
 * already holds a newer authoritative state for this order (its export already
 * succeeded or is in progress), so the client's optimistic view is stale and
 * must resync rather than treat the 409 as a dead end. The body arrives as a
 * raw JSON text string (see `FetchHttpClient.requestJson`), so parse defensively
 * — a malformed or non-matching body is simply "not a staleness conflict".
 */
export function isStaleExportConflict(error: unknown): boolean {
  if (!(error instanceof HttpError) || error.status !== 409) return false
  if (typeof error.body !== "string") return false
  try {
    const parsed = JSON.parse(error.body) as { error?: unknown }
    return (
      typeof parsed?.error === "string" &&
      STALE_EXPORT_CONFLICT_ERRORS.has(parsed.error)
    )
  } catch {
    return false
  }
}

export type FlowChange =
  | { op: "create"; id: string; data: Partial<PreOrderFlow> }
  | { op: "update"; id: string; data: Partial<PreOrderFlow> }
  | { op: "delete"; id: string }

export interface ModifyBatchInput {
  preOrderId: string
  flowChanges: FlowChange[]
  /** Optional pre-order header update (e.g. status flip on confirm). */
  preOrderUpdate?: Partial<PreOrder>
  /**
   * Controls the handler's M3 re-export decision on CONFIRMED parents:
   *  - `"confirm"` → handler fires an explicit M3 export (stamps
   *    `metadata.export.status = in_progress`, emits
   *    `pre-order.confirm-requested`; 409 if a lock is already held).
   *  - `"draft"` → handler SUPPRESSES the implicit re-export even when
   *    the batch resolves to `status = CONFIRMED`. Used by the AI
   *    decline path so a non-material `ai_suggestions[]` flip doesn't
   *    silently re-export the order.
   *  - Omitted → handler INFERS: re-export iff `postBatchStatus ===
   *    CONFIRMED`, otherwise no export.
   */
  intent?: "draft" | "confirm"
}

export interface ModifyBatchResponse {
  preOrder: PreOrder | null
  flows: PreOrderFlow[]
  // BIGSERIAL sync ids round-trip through JSON as strings, so the runtime
  // shape is `string` (not `number`) whenever the id exceeds JS-safe range or
  // the server serialises bigints as text. Typed as the honest union so
  // callers coerce rather than silently doing string arithmetic / comparisons;
  // `notifyServerSyncId` does the `Number()` coercion at the boundary.
  latestPreOrderSyncId: number | string
  latestFlowSyncId: number | string
}

/** Pre-batch state captured so a failed POST can roll the optimistic write back. */
export interface BatchSnapshot {
  /** Prior row for each `flowChanges[i]` (null = create, or row absent). */
  flows: Array<PreOrderFlow | null>
  /** Prior pre-order row — only captured when `preOrderUpdate` is present. */
  preOrder: PreOrder | null
  hadPreOrderUpdate: boolean
}

/**
 * Apply a modify batch to Dexie OPTIMISTICALLY and return a snapshot for revert.
 *
 * This is what makes reject-AI / accept-AI / save-and-validate feel instant: the
 * recap reads from Dexie, so writing the resolved change locally before the POST
 * flips the UI immediately instead of after the 1–2s round-trip. The batch is
 * already the fully-resolved intent (camelCase wire shape, same as the Dexie
 * row), so we apply it directly:
 *  - `update` → merge `data` into the existing flow.
 *  - `delete` → soft-delete (`deletedAt`), mirroring the server.
 *  - `create` → write a row from `data` + identity fields off the parent PO;
 *    the post-POST reconcile overwrites it with the server-authoritative row.
 *  - `preOrderUpdate` → merge into the pre-order row.
 *
 * Confirm/export stays correct without special-casing: a confirm batch carries
 * no status flip in `preOrderUpdate` (the server stamps `export.status` and
 * flips status only on M3 success), so the optimistic apply is a no-op for the
 * header and the "Confirming…" lifecycle is still server-driven.
 */
export async function applyBatchOptimistically(
  storage: StorageAdapter,
  input: ModifyBatchInput
): Promise<BatchSnapshot> {
  const now = new Date().toISOString()
  const flows: Array<PreOrderFlow | null> = []
  let preOrder: PreOrder | null = null
  const hadPreOrderUpdate = input.preOrderUpdate != null

  await storage.transaction(
    [PRE_ORDER_FLOW_TABLE, PRE_ORDER_TABLE],
    async () => {
      const po = await storage.get<PreOrder>(PRE_ORDER_TABLE, input.preOrderId)
      if (hadPreOrderUpdate) preOrder = po ?? null

      for (const change of input.flowChanges) {
        const existing = await storage.get<PreOrderFlow>(
          PRE_ORDER_FLOW_TABLE,
          change.id
        )
        flows.push(existing ?? null)

        if (change.op === "delete") {
          if (existing) {
            await storage.put(PRE_ORDER_FLOW_TABLE, {
              ...existing,
              deletedAt: now,
              updatedAt: now,
            })
          }
        } else if (change.op === "update") {
          if (existing) {
            await storage.put(PRE_ORDER_FLOW_TABLE, {
              ...existing,
              ...change.data,
              updatedAt: now,
            })
          }
        } else {
          // create — fill identity/bookkeeping fields off the parent PO; the
          // reconcile replaces this with the server's canonical row.
          const row = {
            companyId: po?.companyId ?? "",
            userId: po?.userId ?? null,
            syncId: 0,
            deletedAt: null,
            createdAt: now,
            updatedAt: now,
            ...change.data,
            id: change.id,
            preOrderId: input.preOrderId,
          } as unknown as PreOrderFlow
          await storage.put(PRE_ORDER_FLOW_TABLE, row)
        }
      }

      if (hadPreOrderUpdate && po) {
        await storage.put(PRE_ORDER_TABLE, {
          ...po,
          ...input.preOrderUpdate,
          updatedAt: now,
        })
      }
    }
  )

  return { flows, preOrder, hadPreOrderUpdate }
}

/** Undo an optimistic apply after a failed POST: restore each snapshot row. */
export async function revertBatch(
  storage: StorageAdapter,
  input: ModifyBatchInput,
  snapshot: BatchSnapshot
): Promise<void> {
  await storage.transaction(
    [PRE_ORDER_FLOW_TABLE, PRE_ORDER_TABLE],
    async () => {
      for (let i = 0; i < input.flowChanges.length; i++) {
        const change = input.flowChanges[i]!
        const prev = snapshot.flows[i] ?? null
        if (change.op === "create") {
          // The row didn't exist before — drop the optimistic insert.
          await storage.delete(PRE_ORDER_FLOW_TABLE, change.id)
        } else if (prev) {
          await storage.put(PRE_ORDER_FLOW_TABLE, prev)
        }
      }
      if (snapshot.hadPreOrderUpdate && snapshot.preOrder) {
        await storage.put(PRE_ORDER_TABLE, snapshot.preOrder)
      }
    }
  )
}

/**
 * Atomic modify-mode mutation — POSTs flow CRUD + optional pre-order header
 * update to /api/v2/orders/pre-orders/:id/push-batch, where the server runs
 * them in a single transaction.
 *
 * Offline-first: the batch is applied to Dexie OPTIMISTICALLY before the POST
 * (so the recap flips instantly), the server-authoritative rows are written on
 * success, and the optimistic write is rolled back if the POST fails. The
 * echo-shield in the SyncEngine keeps a concurrent SSE event from clobbering the
 * optimistic row before the reconcile lands.
 */
export interface PushBatchOptions {
  /**
   * Fire the POST in the BACKGROUND: `pushBatch` resolves the moment the
   * optimistic apply lands, so the caller's busy/spinner state doesn't span the
   * server round-trip (this is what makes reject-AI / save-modifications feel as
   * instant as delete). On a background failure the optimistic write is still
   * rolled back and `onError` fires. Leave off for ERP confirm/export, where the
   * "Confirming…" spinner SHOULD persist until the server responds.
   */
  background?: boolean
  /** Called after a successful POST + reconcile (both await and background). */
  onSuccess?: (response: ModifyBatchResponse) => void
  /** Called after a failed POST + revert (both await and background). */
  onError?: (error: Error) => void
}

const EMPTY_RESPONSE: ModifyBatchResponse = {
  preOrder: null,
  flows: [],
  latestPreOrderSyncId: 0,
  latestFlowSyncId: 0,
}

export function usePreOrderModifyBatch() {
  const { storage, engine } = useAISync()
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const pushBatch = useCallback(
    async (
      input: ModifyBatchInput,
      opts?: PushBatchOptions
    ): Promise<ModifyBatchResponse> => {
      setIsPending(true)
      setError(null)

      // Transient SSE shield. This optimistic apply writes Dexie directly,
      // bypassing the change queue, so the SyncEngine's echo shield (which
      // reads change-queue entries) can't see these rows as in-flight — a late
      // SSE echo could clobber the optimistic write before/after the reconcile.
      // Register the rows we are about to write for the POST round-trip and
      // release them once we've reconciled (success) or reverted (failure).
      // Only shield what we actually write: flows we touch, and the PO row only
      // when there's a header update (a plain confirm writes neither, so its
      // server-driven "Confirming…" SSE must flow through untouched).
      const flowIds = input.flowChanges.map((c) => c.id)
      const shieldedPoIds =
        input.preOrderUpdate != null ? [input.preOrderId] : []
      engine.shieldEntities("pre-order-flows", flowIds)
      engine.shieldEntities("pre-orders", shieldedPoIds)
      const releaseShield = () => {
        engine.releaseEntities("pre-order-flows", flowIds)
        engine.releaseEntities("pre-orders", shieldedPoIds)
      }

      // Optimistic apply — the recap re-renders from Dexie immediately. This
      // runs BEFORE the try/finally below, so guard it explicitly: a throw here
      // (bad input, Dexie write failure) must clear `isPending` and surface the
      // error, otherwise the caller's spinner / busy gate hangs forever.
      let snapshot: BatchSnapshot
      try {
        snapshot = await applyBatchOptimistically(storage, input)
      } catch (e) {
        releaseShield()
        const err = e instanceof Error ? e : new Error(String(e))
        setError(err)
        setIsPending(false)
        opts?.onError?.(err)
        throw err
      }

      // POST + reconcile (success) or revert (failure). Shared by the awaited
      // and background paths.
      const settle = async (): Promise<ModifyBatchResponse> => {
        try {
          const response = await engine.http.post<ModifyBatchResponse>(
            `/api/v2/orders/pre-orders/${input.preOrderId}/push-batch`,
            {
              flowChanges: input.flowChanges,
              preOrderUpdate: input.preOrderUpdate,
              // Only forward when the caller asked for it — keeps the wire body
              // byte-identical for the existing modify-buffer flushes so the
              // back-compat snapshot tests don't drift.
              ...(input.intent ? { intent: input.intent } : {}),
            }
          )

          // Reconcile Dexie with server-authoritative shapes. A reconcile
          // failure after a successful POST leaves the local rows at the
          // optimistic shape — close enough; SSE replay re-seeds on reconnect.
          try {
            await storage.transaction(
              [PRE_ORDER_FLOW_TABLE, PRE_ORDER_TABLE],
              async () => {
                if (response.flows.length > 0) {
                  await storage.putMany(PRE_ORDER_FLOW_TABLE, response.flows)
                }
                if (response.preOrder) {
                  await storage.put(PRE_ORDER_TABLE, response.preOrder)
                }
              }
            )
          } catch (txErr) {
            console.error(
              "[usePreOrderModifyBatch] post-success reconcile failed; SSE replay will re-seed",
              txErr
            )
          }

          // Push piggyback: the server returns its current visible sync ids for
          // both resources. If either is ahead of our local cursor we missed an
          // SSE event — trigger a surgical resync so the recap can't sit stale
          // (the resync-window drop the STK-483 fix targets). No-op when level.
          engine.notifyServerSyncId("pre-orders", response.latestPreOrderSyncId)
          engine.notifyServerSyncId(
            "pre-order-flows",
            response.latestFlowSyncId
          )

          opts?.onSuccess?.(response)
          return response
        } catch (e) {
          // The server rejected the change — roll the optimistic write back so
          // the recap returns to its pre-click state.
          try {
            await revertBatch(storage, input, snapshot)
          } catch (revertErr) {
            console.error(
              "[usePreOrderModifyBatch] optimistic revert failed",
              revertErr
            )
          }
          // 409 staleness heal: an `export_already_succeeded` /
          // `export_in_progress` rejection means the server confirmed/exported
          // this order out from under us — the recap is stale. Resync both
          // resources so it flips to confirmed without a reload, then let the
          // original error propagate unchanged (this never swallows it).
          if (isStaleExportConflict(e)) {
            engine.requestResync("pre-orders")
            engine.requestResync("pre-order-flows")
          }
          const err = e instanceof Error ? e : new Error(String(e))
          setError(err)
          opts?.onError?.(err)
          throw err
        } finally {
          // Reconcile / revert has written authoritative (or restored) rows;
          // drop the transient shield so live SSE flows normally again. Any
          // echo dropped during the window is recovered from the recorded
          // floor by `releaseEntities`.
          releaseShield()
        }
      }

      // Background: unblock the caller now (optimistic apply already landed) and
      // let the POST settle on its own; errors surface via onError + revert.
      if (opts?.background) {
        setIsPending(false)
        void settle().catch(() => {})
        return EMPTY_RESPONSE
      }

      try {
        return await settle()
      } finally {
        setIsPending(false)
      }
    },
    [storage, engine]
  )

  return { pushBatch, isPending, error }
}
